import { FormEvent, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  Button,
  Callout,
  CheckCircleIcon,
  Label,
  LockIcon,
  MoonIcon,
  SunIcon,
  TextInput,
} from '../components/ui';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../hooks/useAuth';
import { t } from '../i18n';
import { ApiError, consumeSessionExpiredFlag } from '../services/api';

export function LoginPage() {
  const { isAuthenticated, login } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sessionNotice, setSessionNotice] = useState<string | null>(() =>
    consumeSessionExpiredFlag() ? t().auth.sessionExpired : null,
  );
  const [loading, setLoading] = useState(false);
  const labels = t().auth;
  const nav = t().nav;
  const app = t().app;

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSessionNotice(null);

    if (!email.trim()) {
      setError(labels.requiredEmail);
      return;
    }
    if (!password) {
      setError(labels.requiredPassword);
      return;
    }

    setLoading(true);
    try {
      await login(email.trim(), password);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 429) {
          setError(err.message);
        } else {
          setError(err.message || labels.invalidCredentials);
        }
      } else {
        setError(labels.invalidCredentials);
      }
    } finally {
      setLoading(false);
    }
  }

  const features = [
    labels.featureBalances,
    labels.featureInvoices,
    labels.featureReports,
  ];

  return (
    <div className="flex min-h-screen bg-surface text-ink">
      {/* Brand panel — desktop */}
      <aside className="relative hidden w-[46%] flex-col justify-between overflow-hidden bg-login-panel px-10 py-10 text-white lg:flex xl:w-[48%] xl:px-14">
        <div>
          <div className="inline-flex items-center gap-2.5 rounded-lg bg-white/10 px-3 py-2 ring-1 ring-white/10">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-teal-400/20 text-sm font-bold text-teal-200">
              BF
            </span>
            <span className="text-sm font-semibold tracking-wide">
              {app.name}
            </span>
          </div>
        </div>

        <div className="max-w-md">
          <h1 className="font-display text-4xl font-semibold leading-tight tracking-tight xl:text-5xl">
            {app.name}
          </h1>
          <p className="mt-4 text-base leading-relaxed text-slate-300">
            {labels.panelTagline}
          </p>
          <ul className="mt-10 space-y-3.5">
            {features.map((feature) => (
              <li key={feature} className="flex items-start gap-3 text-sm text-slate-200">
                <CheckCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-teal-300" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className="text-xs text-slate-400">{app.subtitle}</p>
      </aside>

      {/* Form panel */}
      <div className="relative flex flex-1 flex-col">
        <div className="absolute right-4 top-4 z-10 sm:right-6 sm:top-6">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={toggleTheme}
            aria-label={theme === 'dark' ? nav.themeLight : nav.themeDark}
            title={theme === 'dark' ? nav.themeLight : nav.themeDark}
          >
            {theme === 'dark' ? (
              <SunIcon className="h-4 w-4" />
            ) : (
              <MoonIcon className="h-4 w-4" />
            )}
          </Button>
        </div>

        {/* Mobile brand strip */}
        <div className="border-b border-line bg-login-panel px-6 py-8 text-white lg:hidden">
          <div className="font-display text-2xl font-semibold">{app.name}</div>
          <p className="mt-1 text-sm text-slate-300">{app.subtitle}</p>
        </div>

        <div className="flex flex-1 items-center justify-center px-4 py-10 sm:px-8">
          <div className="w-full max-w-[24rem]">
            <div className="mb-8">
              <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-brand-soft text-brand dark:bg-brand-soft dark:text-sky-300">
                <LockIcon className="h-5 w-5" />
              </div>
              <h2 className="text-2xl font-semibold tracking-tight text-ink">
                {labels.loginWelcome}
              </h2>
              <p className="mt-1.5 text-sm text-ink-muted">{labels.loginHint}</p>
            </div>

            <form
              onSubmit={handleSubmit}
              className="space-y-4 rounded-xl border border-line bg-surface-card p-6 shadow-bf sm:p-7"
            >
              {sessionNotice ? (
                <Callout variant="warning">{sessionNotice}</Callout>
              ) : null}

              <div>
                <Label htmlFor="email">{labels.email}</Label>
                <TextInput
                  id="email"
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  showClearButton={false}
                  placeholder={labels.emailPlaceholder}
                />
              </div>

              <div>
                <div className="mb-1.5 flex items-center justify-between gap-2">
                  <Label htmlFor="password">{labels.password}</Label>
                  <Link
                    to="/forgot-password"
                    className="text-xs font-medium text-teal-700 hover:underline dark:text-teal-300"
                  >
                    {labels.forgotPassword}
                  </Link>
                </div>
                <TextInput
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  showClearButton={false}
                  placeholder={labels.passwordPlaceholder}
                />
              </div>

              {error ? <Callout variant="danger">{error}</Callout> : null}

              <Button type="submit" className="w-full" loading={loading}>
                <LockIcon className="h-4 w-4" />
                {labels.submit}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
